'use client';

import { memo } from 'react';
import { BellIcon, PencilIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'next-i18next';
import { Anchor } from '@components/common';
import { cn } from '@utils/cn';
import type { JobSearchQueryParams } from '@type/filters';
import { Tags } from '@components/templates/my-jobs/Card/partials/Tags';
import { getI18nLinkHref } from '@services/i18nPaths';
import { LocateFixed } from 'lucide-react';

interface CompactJobCardProps {
  job: ApiSchema<'MyJobCard'>;
  isSelected?: boolean;
  onSelect?: (job: ApiSchema<'MyJobCard'>) => void;
  /** When provided, the NEW button calls this with status=1 (new applicants) instead of navigating. */
  onTagClick?: (job: ApiSchema<'MyJobCard'>, status: number) => void;
  /** Optional href builder — used in MC context to produce /[locale]/mc/... URLs. */
  buildTagHref?: (jobSlug: string, status: number) => string;
  /** Locale used to build edit and applicant URLs. When provided, EDIT and NEW buttons are shown. */
  locale?: Locale;
  /**
   * Callback for the MAP action button. Receives query params derived from the job's data.
   * Provided by a parent outside the import cycle (e.g. MissionControl).
   */
  onOpenMap?: (queryParams?: Partial<JobSearchQueryParams>) => void;
}

const getStatusDotClass = (active: boolean | undefined, expiresIn: number) => {
  if (!active) return 'bg-ink-light';
  return expiresIn > 7 ? 'bg-secondary' : 'bg-warning';
};

const ICON_BTN_CLASS = 'flex size-8 items-center justify-center text-ink transition-colors hover:text-primary';

// Set to true to re-enable the active/expiry status dot next to each job title.
const SHOW_STATUS_DOT = false;

/**
 * @description Compact job view for the company jobs sidebar.
 * Shows job title and three icon action buttons: MAP, EDIT, NEW.
 */
const CompactJobCard = (props: CompactJobCardProps) => {
  const {
    job, isSelected, onSelect, onTagClick, buildTagHref, locale, onOpenMap,
  } = props;

  const { t } = useTranslation('common');
  const flowHref = locale ? getI18nLinkHref(`/mc/job/${job.slug}/applicants/flow`, locale) : `/mc/job/${job.slug}/applicants/flow`;
  const editHref = locale ? getI18nLinkHref(`/mc/job-add/edit/${job.slug}`, locale) : `/mc/job-add/edit/${job.slug}`;

  const handleOpenMap = () => {
    onOpenMap?.({
      ...(job.title && { keyword: job.title }),
      ...(job.location && { location: [job.location] }),
    });
  };

  const handleNew = () => {
    if (onTagClick) {
      onTagClick(job, 1);
    }
  };

  const newCount = job.numberOfNewApplicants ?? 0;
  const newLabel = t('my-jobs.card.tags.new') || 'New';
  const newTitle = newCount > 0 ? `${newLabel} (${newCount})` : newLabel;

  const newApplicantsHref = buildTagHref
    ? buildTagHref(job.slug!, 1)
    : `/${locale ?? 'ro'}/mc/job/${job.slug}/applicants?status=1`;

  return (
    <div
      className={cn(
        'flex items-center gap-2 rounded-lg py-2 transition-colors',
        isSelected && 'bg-primary/5',
      )}
    >
      {/* Status dot — controlled by SHOW_STATUS_DOT flag */}
      {SHOW_STATUS_DOT && (
        <span
          className={cn(
            'size-2.5 shrink-0 rounded-full',
            getStatusDotClass(job.active, job.expiresIn),
          )}
        />
      )}

      {/* Job title — fills remaining space, truncates on overflow */}
      <Anchor
        styling="none"
        href={flowHref}
        className="flex items-start gap-2"
        onClick={(e) => {
          if (onSelect) {
            // In Mission Control context: delegate navigation to the parent handler
            e.preventDefault();
            onSelect(job);
          } else {
            e.stopPropagation();
          }
        }}
      >
        <span className={cn(
          'block truncate text-sm font-semibold text-ink hover:text-primary',
          isSelected && 'text-primary',
        )}
        >
          {job.title}
        </span>
      </Anchor>

      {/* Icon action buttons: MAP · EDIT · NEW */}
      {onOpenMap && (
        <button
          type="button"
          aria-label={job.location ?? t('add.job.open.map')}
          title={job.location ?? t('add.job.open.map')}
          className={ICON_BTN_CLASS}
          onClick={handleOpenMap}
        >
          <LocateFixed className="size-5" />
        </button>
      )}

      {locale && job.slug && (
        <Anchor
          href={`/${locale}/mc/job-add/edit/${job.slug}`}
          styling="none"
          aria-label={t('my-jobs.card.actions.edit')}
          title={t('my-jobs.card.actions.edit')}
          className={ICON_BTN_CLASS}
          onClick={(e) => e.stopPropagation()}
        >
          <PencilIcon className="size-5" />
        </Anchor>
      )}

      {/* NEW — opens new applicants folder (status=1) */}
      {locale && job.slug && (
        onTagClick ? (
          <button
            type="button"
            aria-label={newTitle}
            title={newTitle}
            className={cn(ICON_BTN_CLASS, 'relative')}
            onClick={handleNew}
          >
            <BellIcon className="size-5" />
            {newCount > 0 && (
              <span className="absolute -right-1 -top-1 flex size-4 items-center justify-center rounded-full bg-ink text-[10px] font-bold text-surface">
                {newCount}
              </span>
            )}
          </button>
        ) : (
          <Anchor
            href={newApplicantsHref}
            styling="none"
            aria-label={newTitle}
            title={newTitle}
            className={cn(ICON_BTN_CLASS, 'relative')}
            onClick={(e) => e.stopPropagation()}
          >
            <BellIcon className="size-5" />
            {newCount > 0 && (
              <span className="absolute -right-1 -top-1 flex size-4 items-center justify-center rounded-full bg-ink text-[10px] font-bold text-surface">
                {newCount}
              </span>
            )}
          </Anchor>
        )
      )}
    </div>
  );
};

export const MemoizedCompactJobCard = memo(CompactJobCard);
